import classNames from 'classnames';
import { differenceInSeconds } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import StyledButton from '../buttons/StyledButton';
import PageTitle from '../partials/PageTitle';
import ResultsQuestion from '../partials/ResultsQuestion';

import styles from '../../../styles/pages/results-default.module.css';

interface ResultsDefaultProps {
    page: Page;
}

function ResultsDefault({ page = null }: ResultsDefaultProps) {
    const { title: pageTitle = null, games = null, participations = null } = page || {};

    const gameGroups = useMemo(() => {
        return (games || []).reduce((acc, game) => {
            const { subtitle = null } = game || {};
            if (subtitle !== null) {
                if (!acc[subtitle]) {
                    acc[subtitle] = {
                        id: subtitle,
                        label: subtitle,
                        startAt: game.startAt,
                        games: [],
                    };
                }
                acc[subtitle].games.push(game);
            }
            return acc;
        }, {});
    }, [games]);

    const { initialGroup = null, initialGame = null } = useMemo(() => {
        const [firstKey = null] = Object.keys(gameGroups);
        const { id: initialGroupId = null, games = [] } = gameGroups[firstKey] || {};
        const [firstGame = null] = games || [];
        const { id: initialGameId = null } = firstGame || {};
        return { initialGroup: initialGroupId, initialGame: initialGameId };
    }, [gameGroups]);

    const totalPoints = useMemo(() => {
        return (participations || []).reduce(
            (acc: number, { points = 0 }: { points?: number }) => acc + points,
            0,
        );
    }, [participations]);

    // Old way to stay compat with question groups
    const [selectedQuestionsGroupIndex, setSelectedQuestionsGroupIndex] = useState(initialGroup);

    const [selectedGameGroupId, setSelectedGameGroupId] = useState(initialGroup);
    const [gameId, setGameId] = useState(initialGame);

    const selectedGroup = useMemo(
        () => gameGroups[selectedGameGroupId] || null,
        [gameGroups, selectedGameGroupId],
    );
    const { games: finalGames = [] } = selectedGroup || {};

    const currentGame = useMemo(
        () => (games || []).find(({ id = null }) => id === gameId) || {},
        [games, gameId],
    );

    const {
        id: selectedGameId = null,
        questions: gameQuestions = [],
        questionsGroups = [],
    } = currentGame || {};

    const participationForGame = useMemo(() => {
        const sortedParticipations = [...(participations || [])].sort(
            ({ updatedAt: a }, { updatedAt: b }) => new Date(b) - new Date(a),
        );
        return sortedParticipations.find(({ gameId }) => gameId === selectedGameId);
    }, [participations, selectedGameId]);
    const { points: currentPoints = null } = participationForGame || {};

    const questions = useMemo(() => {
        // In case by group
        const { questions: questionsOfGroup = null } =
            questionsGroups[selectedQuestionsGroupIndex] || {};

        return (questionsOfGroup || gameQuestions || []).map((question) => {
            const { id: questionId = null, duration: questionTimeLimit = null } = question;
            const { answers: participationAnswersForGame = null } = participationForGame || {};

            const {
                valid = null,
                validated = null,
                points = null,
                createdAt: answerCreatedAtStr = null,
                validatedAt: answerValidatedAtStr = null,
            } = (participationAnswersForGame || []).find(
                ({ question: answerQuestionId }) => answerQuestionId === questionId,
            ) || {};

            const answerCreatedAt =
                answerCreatedAtStr !== null ? new Date(answerCreatedAtStr) : null;
            const answerValidatedAt =
                answerValidatedAtStr !== null ? new Date(answerValidatedAtStr) : null;
            const answerDuration =
                answerCreatedAt !== null && answerValidatedAt !== null
                    ? differenceInSeconds(answerValidatedAt, answerCreatedAt)
                    : null;

            return {
                ...question,
                valid,
                validated,
                points,
                answerDuration: answerDuration !== null ? answerDuration : questionTimeLimit,
            };
        });
    }, [games, participations, selectedGameId, selectedQuestionsGroupIndex, gameQuestions]);

    const validatedQuestionsCount = useMemo(
        () =>
            [currentGame].reduce(
                (acc, { questions: questionsOfGroup = [] }) =>
                    acc + questionsOfGroup.filter((c) => c.validated).length,
                0,
            ),
        [currentGame],
    );

    const correctQuestionsCount = useMemo(
        () =>
            [currentGame].reduce(
                (acc, { questions: questionsOfGroup = [] }) =>
                    acc + questionsOfGroup.filter((c) => c.valid).length,
                0,
            ),
        [currentGame],
    );

    const totalQuestionsCount = useMemo(
        () =>
            [currentGame].reduce(
                (acc, { questions: questionsOfGroup = [] }) => acc + questionsOfGroup.length,
                0,
            ),
        [currentGame],
    );

    const allGamesCorrectQuestionsCount = useMemo(
        () =>
            (games || []).reduce(
                (acc, { questions: questionsOfGroup = [] }) =>
                    acc + questionsOfGroup.filter((c) => c.valid).length,
                0,
            ),
        [games],
    );

    const allGamesTotalQuestionsCount = useMemo(
        () =>
            (games || []).reduce(
                (acc, { questions: questionsOfGroup = [] }) => acc + questionsOfGroup.length,
                0,
            ),
        [games],
    );

    const onGroupClicked = useCallback(
        (id) => {
            setSelectedGameGroupId(id);
            const { games = [] } = gameGroups[id] || {};
            const [firstGame = null] = games || [];
            const { id: newGameId = null } = firstGame || {};
            setGameId(newGameId);
        },
        [setSelectedGameGroupId, setGameId, gameGroups],
    );

    const onGameClicked = useCallback(
        (id) => {
            setGameId(id);
        },
        [setGameId],
    );

    return (
        <div className={classNames(styles.container, 'noHeader')}>
            <div className={styles.header}>
                <PageTitle className={styles.title} title={pageTitle} />
                <div className={styles.circle}>
                    {allGamesCorrectQuestionsCount.toString().padStart(2, '0')}/
                    {allGamesTotalQuestionsCount.toString().padStart(2, '0')}
                </div>
                <div className={styles.totalPoints}>
                    <FormattedMessage defaultMessage="Total points :" description="Header label" />{' '}
                    <div className={styles.points}>
                        {totalPoints || 0} <span>pts</span>
                    </div>
                </div>
                <div className={styles.center}>
                    <div className={styles.tabs}>
                        {Object.keys(gameGroups).map((groupId) => {
                            const { id, label = null } = gameGroups[groupId];
                            return (
                                <StyledButton
                                    key={id}
                                    type="button"
                                    className={classNames(styles.tab, {
                                        [styles.active]: id === selectedGameGroupId,
                                    })}
                                    onClick={() => onGroupClicked(id)}
                                >
                                    {label || id}
                                </StyledButton>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className={styles.inner}>
                <div className={styles.center}>
                    {finalGames !== null && finalGames.length > 1 ? (
                        <div className={styles.tabs}>
                            {(finalGames || []).map((game) => {
                                const { id, title } = game || {};
                                return (
                                    <StyledButton
                                        key={id}
                                        type="button"
                                        className={classNames(styles.tab, {
                                            [styles.active]: id === selectedGameId,
                                        })}
                                        onClick={() => onGameClicked(id)}
                                    >
                                        {title}
                                    </StyledButton>
                                );
                            })}
                        </div>
                    ) : null}

                    {questionsGroups !== null && questionsGroups.length > 1 ? (
                        <div className={styles.tabs}>
                            {(questionsGroups || []).map(({ id, label = null }, index) => {
                                return (
                                    <StyledButton
                                        key={`${selectedGameId || ''}_${id}`}
                                        type="button"
                                        className={classNames(styles.tab, {
                                            [styles.active]: index === selectedQuestionsGroupIndex,
                                        })}
                                        onClick={() => setSelectedQuestionsGroupIndex(index)}
                                    >
                                        {label}
                                    </StyledButton>
                                );
                            })}
                        </div>
                    ) : null}

                    {validatedQuestionsCount > 0 ? (
                        <div className={styles.stats}>
                            <div className={styles.ratio}>
                                {correctQuestionsCount.toString().padStart(2, '0')}/
                                {totalQuestionsCount.toString().padStart(2, '0')}
                            </div>
                            <div className={styles.totalPoints}>
                                <div className={styles.points}>
                                    {currentPoints || 0} <span>pts</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.stats}>
                            <FormattedMessage
                                defaultMessage="Finish this game to see your results"
                                description="No questions answered"
                            />
                        </div>
                    )}

                    <div className={styles.questions}>
                        {(questions || [])
                            .filter(({ validated = false }) => validated)
                            .map((question, index) => (
                                <ResultsQuestion
                                    number={(index + 1).toString().padStart(2, '0')}
                                    key={question.id}
                                    question={question}
                                    withExplanation
                                    open
                                />
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResultsDefault;
